import type { WebsiteProductOfferingPlugin, WebsiteProductOfferingPluginModule } from "./types";

type OfferingManifestModule = {
  id: string;
  websitePluginModule: string;
};

type OfferingManifestJsonModule = {
  default: OfferingManifestModule;
};

const offeringManifestLoaders = import.meta.glob<OfferingManifestJsonModule>(
  "../../../../packages/*/manifest.json"
);
const offeringWebsitePluginLoaders = import.meta.glob<WebsiteProductOfferingPluginModule>(
  "../../../../packages/*/src/offering-plugin.tsx"
);
const eagerOfferingManifestModules = import.meta.glob<OfferingManifestJsonModule>(
  "../../../../packages/*/manifest.json",
  { eager: true }
);
const eagerOfferingWebsitePluginModules = import.meta.glob<WebsiteProductOfferingPluginModule>(
  "../../../../packages/*/src/offering-plugin.tsx",
  { eager: true }
);

function resolvePluginModulePath(manifestPath: string) {
  return manifestPath.replace(/manifest\.json$/, "src/offering-plugin.tsx");
}

function selectLoadersFromManifests(
  manifestModules: Array<{ manifestPath: string; manifest: OfferingManifestModule }>,
  loaders: Record<string, () => Promise<WebsiteProductOfferingPluginModule>>
) {
  return manifestModules
    .map(({ manifestPath, manifest }) => {
      // Keep manifest-driven discovery while loading bundle-safe module paths.
      if (!manifest.websitePluginModule?.trim()) {
        return null;
      }
      return loaders[resolvePluginModulePath(manifestPath)] ?? null;
    })
    .filter((loader): loader is NonNullable<typeof loader> => Boolean(loader));
}

function resolveEagerPlugins() {
  const manifestModules = Object.entries(eagerOfferingManifestModules).map(
    ([manifestPath, module]) => ({
      manifestPath,
      manifest: module.default,
    })
  );

  return manifestModules
    .map(({ manifestPath, manifest }) => {
      if (!manifest.websitePluginModule?.trim()) {
        return null;
      }
      return eagerOfferingWebsitePluginModules[resolvePluginModulePath(manifestPath)] ?? null;
    })
    .filter((module): module is NonNullable<typeof module> => Boolean(module))
    .map(resolvePlugin);
}

function resolvePlugin(module: WebsiteProductOfferingPluginModule) {
  if ("default" in module) {
    return module.default;
  }

  return module.plugin;
}

export async function loadWebsiteOfferingPlugins() {
  const manifestModules = await Promise.all(
    Object.entries(offeringManifestLoaders).map(async ([manifestPath, loader]) => ({
      manifestPath,
      manifest: (await loader()).default,
    }))
  );
  const modules = await Promise.all(
    selectLoadersFromManifests(manifestModules, offeringWebsitePluginLoaders).map((loader) =>
      loader()
    )
  );
  return modules.map(resolvePlugin);
}

export function getInitialWebsiteOfferingPlugins(): WebsiteProductOfferingPlugin[] {
  // Offering plugins are discovered from static manifests bundled at build time
  // (import.meta.glob eager), so they are available synchronously. Returning them
  // as the initial state means offerings render on first paint — no empty-then-
  // populate flash for real users, and deterministic content for the prerender
  // step (which snapshots the first hydrated frame). The provider still refreshes
  // from /api/website/offerings afterward to pick up any server-side changes.
  return resolveEagerPlugins();
}

export function getPrimaryWebsiteOffering(offerings: WebsiteProductOfferingPlugin[]) {
  return offerings[0];
}

export function getSecondaryWebsiteOfferings(offerings: WebsiteProductOfferingPlugin[]) {
  return offerings.slice(1);
}

export function getWebsiteOfferingById(
  offerings: WebsiteProductOfferingPlugin[],
  offeringId: string
) {
  return offerings.find((offering) => offering.id === offeringId);
}
